/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import { apiUrl } from '../../../utilities/apiUrl';
import {
  sortByEmpId,
  sortByCheckInDate,
  sortByCheckOutDate,
  sortByComputedTime,
} from '../../../utilities/sortBy';
import { useEffect, useState } from 'react';
import {
  format,
  isBefore,
  minutesToHours,
  subWeeks,
  subMonths,
  subDays,
} from 'date-fns';
import {
  TableHead,
  TableRow,
  TableBody,
  IconButton,
  Grid,
  MenuItem,
  Button,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  Work as WorkIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  GeneralModal,
  ModalButton,
  QueryContainer,
  OutlinedButton,
  ContainedButton,
  SelectDropdown,
  SuccessModal,
  AddTimesheetModal,
} from '../../index';
import { CSVLink } from 'react-csv';
import {
  GeneralTable,
  CustomTableCell,
  CustomTableRow,
  ActionTableCell,
  CustomTextField,
} from '../../index';
import { useOutletContext } from 'react-router-dom';

const downloadStyle = {
  bgcolor: '#84d8c5',
  color: '#FFF',
  width: 180,
  transition: 'background-color 0.3s ease-in',
  '&:hover': {
    bgcolor: '#57af9b !important',
  },
};

export const QueryTable = (props) => {
  const [empId] = useOutletContext();
  const timeFormat = 'hh:mm:ss a';
  const data = props.timesheetData;
  const empData = props.allEmpData;
  const empActive = props.timesheetActive;
  const [entryPerPage, setEntryPerPage] = useState(10);
  const workDescRegex = /[A-Za-z0-9.,:;()-\s]{0,100}/;
  const workSetup = { 1: 'On-site', 2: 'WFH', 3: 'Deployed' };
  const [timesheetList, setTimesheetList] = useState([]);
  const [slice, setSlice] = useState([0, entryPerPage]);
  const [sortBy, setSortBy] = useState('date');
  const [order, setOrder] = useState('desc');
  // const [filter, setFilter] = useState([]);
  const [dateRange, setDateRange] = useState('');
  const [pageCount, setPageCount] = useState(
    Math.floor(timesheetList.length / entryPerPage)
  );
  // const [searchValue, setSearchValue] = useState("");
  const [timeId, setTimeId] = useState('');
  const [enteredCheckIn, setEnteredCheckIn] = useState(new Date());
  const [enteredCheckOut, setEnteredCheckOut] = useState(new Date());
  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [cleanTimesheet, setCleanTimesheet] = useState([]);
  const [addTimesheetModalOpen, setAddTimesheetModalOpen] = useState(false);
  const [addCheckIn, setAddCheckIn] = useState(new Date());
  const [empid, setEmpId] = useState('');
  const [isDescending, setIsDescending] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const [addSuccessOpen, setAddSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isSuperAdmin, setIsSuperAdmin] = useState('');
  const [selectedWorkSetup, setSelectedWorkSetup] = useState('');
  const [isSelected, setIsSelected] = useState(true);
  const [enteredWorkDesc, setEnteredWorkDesc] = useState('');
  const [isAllowed, setIsAllowed] = useState(true);
  const [addWorkSetup, setAddWorkSetup] = useState('');
  const [hasWorksetup, setHasWorksetup] = useState(false);
  const [addWorkDesc, setAddWorkDesc] = useState('');
  const [isValidDesc, setIsValidDesc] = useState(true);
  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
  const [checkInOutModalOpen, setCheckInOutModalOpen] = useState(false);
  const [newSuccessOpen, setNewSuccessOpen] = useState(false);
  const [restrictCheckIn, setRestrictCheckIn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  //Reset to page 1 when changing the number of entries
  useEffect(() => {
    setCurrentPage(1);
    setPageCount(Math.floor(timesheetList.length / entryPerPage));
    setSlice([0, entryPerPage]);
  }, [entryPerPage, timesheetList]);

  // GET EMPLOYEE ROLE
  useEffect(() => {
    fetch(apiUrl('/employee/' + empId), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setIsSuperAdmin(data.data.role === 'SUPER_ADMIN' ? true : false);
      });
  }, [empId]);

  useEffect(() => {
    cleanDataForExport();
    if (dateRange === '') {
      setTimesheetList(sortByCheckInDate(data));
      setIsDescending(true);
    }
    setPageCount(Math.floor(timesheetList.length / entryPerPage));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesheetList, data]);

  const cleanDataForExport = () => {
    const cleanData = timesheetList.map((ts) => ({
      employee_id: ts.empid,
      lastname: ts.lastname,
      firstname: ts.firstname,
      checkin: format(new Date(ts.checkin), 'MM/dd/yyyy HH:mm:ss'),
      checkout: format(new Date(ts.checkout), 'MM/dd/yyyy HH:mm:ss'),
      computed_time: Math.floor((ts.roundedtime / 60) * 1000) / 1000,
      work_setup: workSetup[ts.worksetup],
      work_description: ts.workdesc,
    }));

    setCleanTimesheet(cleanData);
  };

  const handlePageChange = (e, page) => {
    setCurrentPage(page);
    let currPage = page - 1;
    if (page === 1) {
      setSlice([0, entryPerPage]);
    } else {
      setSlice([currPage * entryPerPage, page * entryPerPage]);
    }
  };

  const handleSortByChange = (event) => {
    let val = event.target.value;
    setSortBy(val);

    // descending default
    if (val === 'empid') {
      isDescending == true
        ? setTimesheetList(sortByEmpId(timesheetList).reverse()) // ascending order default
        : setTimesheetList(sortByEmpId(timesheetList));
    } else if (val === 'checkin' || val === 'date') {
      isDescending == true
        ? setTimesheetList(sortByCheckInDate(timesheetList))
        : setTimesheetList(sortByCheckInDate(timesheetList).reverse());
    } else if (val === 'time') {
      isDescending == true
        ? setTimesheetList(sortByComputedTime(timesheetList))
        : setTimesheetList(sortByComputedTime(timesheetList).reverse());
    } else if (val === 'checkout') {
      isDescending == true
        ? setTimesheetList(sortByCheckOutDate(timesheetList))
        : setTimesheetList(sortByCheckOutDate(timesheetList).reverse());
    }

    setCurrentPage(1);
    setPageCount(Math.floor(timesheetList.length / entryPerPage));
    setSlice([0, entryPerPage]);

    cleanDataForExport();
  };

  const descendingOrder = (val) => {
    setIsDescending(true);
    if (val === 'empid') {
      setTimesheetList(sortByEmpId(timesheetList).reverse());
    } else if (val === 'checkin' || val === 'date') {
      setTimesheetList(sortByCheckInDate(timesheetList));
    } else if (val === 'time') {
      setTimesheetList(sortByComputedTime(timesheetList));
    } else if (val === 'checkout') {
      setTimesheetList(sortByCheckOutDate(timesheetList));
    }
  };

  const handleOrderChange = (event) => {
    let val = event.target.value;
    setOrder(val);

    if (val === 'asc' && isDescending) {
      setTimesheetList(timesheetList.reverse());
      setIsDescending(false);
    } else if (val === 'desc' && sortBy === '' && dateRange === '') {
      setTimesheetList(sortByCheckInDate(data));
      setIsDescending(true);
    } else if (val === 'desc' && sortBy === '' && dateRange !== '') {
      setTimesheetList(sortByCheckInDate(timesheetList));
      setIsDescending(true);
    } else if (val === 'desc' && sortBy !== '') {
      descendingOrder(sortBy);
    }

    setCurrentPage(1);
    setPageCount(Math.floor(timesheetList.length / entryPerPage));
    setSlice([0, entryPerPage]);

    cleanDataForExport();
  };

  const handleDateRangeChange = (event) => {
    let val = event.target.value;
    setDateRange(val);
    const today = new Date();
    const oneWeek = subWeeks(today, 1);
    const twoWeeks = subWeeks(today, 2);
    const oneMonth = subMonths(today, 1);
    const threeMonths = subMonths(today, 3);
    let startDate = new Date();
    let endDate = new Date();

    const filterDate = data.filter((a) => {
      const date = new Date(a.checkin);
      endDate = today;

      val === '3months'
        ? (startDate = threeMonths)
        : val === '30days'
          ? (startDate = oneMonth)
          : val === '14days'
            ? (startDate = twoWeeks)
            : val === '7days'
              ? (startDate = oneWeek)
              : setTimesheetList(props.cutoff);
      return date >= startDate && date <= endDate;
    });

    setTimesheetList(filterDate);
    // setFilter(filterDate);

    if (val === 'cutoff') {
      setTimesheetList(props.cutoff);
    }

    cleanDataForExport();
  };

  const handleReset = () => {
    setTimesheetList(sortByCheckInDate(data));
    setSortBy('');
    setOrder('');
    setDateRange('');
    // setSearchValue("");
  };

  const handleEditModalOpen = (event) => {
    const currentTime = timesheetList[event.currentTarget.id];

    setTimeId(currentTime._id);
    setEnteredCheckIn(currentTime.checkin);
    setSelectedEmpId(currentTime.empid);
    setEnteredCheckOut(currentTime.checkout);
    setEnteredWorkDesc(currentTime.workdesc);
    if (currentTime.worksetup) {
      setSelectedWorkSetup(currentTime.worksetup);
    } else {
      // handle items without work setup
      setSelectedWorkSetup('');
      setIsSelected(false);
    }
    setEditModalOpen(true);
  };

  const handleSaveEditedTimesheet = () => {
    const editedInfo = {
      empid: selectedEmpId,
      checkin: enteredCheckIn,
      checkout: enteredCheckOut,
      worksetup: selectedWorkSetup,
      workdesc: enteredWorkDesc,
    };

    fetch(apiUrl('/timesheet/' + timeId), {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(editedInfo),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setEditModalOpen(false);
          setResultModalOpen(true);
        } else {
          alert(data.message);
        }
      });
  };

  const handleCheckInEmployee = () => {
    fetch(apiUrl('/timesheet/check-in'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        empid: empid,
        checkin: addCheckIn,
        worksetup: addWorkSetup,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setAddTimesheetModalOpen(false);
          setSuccessMessage(
            'You have successfully checked in employee: ' + empid
          );
          setAddSuccessOpen(true);
        } else {
          setRestrictCheckIn(true);
        }
      })
      .catch((err) => {
        console.log(err);
      });
    setIsLoading(false);
  };

  const handleCheckOutEmployee = () => {
    fetch(apiUrl('/timesheet/check-out'), {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ empid: empid, workdesc: addWorkDesc }),
    })
      .then((response) => response.json())
      .then((data) => {
        setAddTimesheetModalOpen(false);
        setSuccessMessage(
          'You have successfully checked out employee: ' + empid
        );
        setAddSuccessOpen(true);
      })
      .catch((err) => {
        console.log(err);
      });
    setIsLoading(false);
  };

  const isEmployeeCheckIn = () => {
    const enteredEmp = empData.filter((emp) => {
      return emp._id === empid;
    });

    if (enteredEmp[0].active === true) {
      const activeEmployees = empActive.filter((data) => {
        return data.checkout === null;
      });

      const checkInTime = activeEmployees.filter((data) => {
        return data.empid === empid;
      });

      setAddCheckIn(new Date(checkInTime[0].checkin));
    }

    enteredEmp[0].active === true ? setIsActive(true) : setIsActive(false);
  };

  // adding a new timesheet is handled by calling 4 timesheet APIs: checkin, find, checkout, edit
  const handleAddNewTimesheet = async (input) => {
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);

    // create a timesheet
    const checkInRequest = await fetch(apiUrl('/timesheet'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        empid: input.empId,
        checkin: input.checkIn,
        checkout: input.checkOut,
        worksetup: input.workSetup, // honor the Work Setup chosen in the modal
        workdesc: input.workDesc,
      }),
    });
    const checkInResponse = await checkInRequest.json();

    // open success modal
    if (checkInResponse.success) {
      setIsSubmitting(false);
      setNewSuccessOpen(true);
    }

    // close Add Timesheet modal
    setAddTimesheetModalOpen(false);
  };

  const handleLoading = () => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);

    setTimeout(() => {
      if (isActive) {
        handleCheckOutEmployee();
      } else {
        handleCheckInEmployee();
      }
    }, 300);
  };

  return (
    <>
      <QueryContainer>
        <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
          <Grid item>
            <SelectDropdown
              sx={{ minWidth: 200 }}
              size="small"
              label="Sort by"
              value={sortBy}
              onChange={handleSortByChange}
            >
              <MenuItem value={'empid'}>Employee ID</MenuItem>
              <MenuItem value={'date'}>Date</MenuItem>
              {/* [EPSv5_Manage-TimesheetSortBy] Commented out. Will not be implemented. */}
              {/* <MenuItem value={"checkin"}>Check In</MenuItem>
              <MenuItem value={"checkout"}>Check Out</MenuItem> */}
              <MenuItem value={'time'}>Computed Time</MenuItem>
            </SelectDropdown>
          </Grid>
          <Grid item sx={{ ml: 3 }}>
            <SelectDropdown
              sx={{ minWidth: 200 }}
              size="small"
              label="Order"
              value={order}
              onChange={handleOrderChange}
            >
              <MenuItem value={'asc'}>Ascending</MenuItem>
              <MenuItem value={'desc'}>Descending</MenuItem>
            </SelectDropdown>
          </Grid>
          {/* [EPSv5_Dashboard-queryTable]
          <Grid item sx={{ ml: 3 }}>
            <SelectDropdown
              sx={{ minWidth: 200 }}
              size="small"
              label="Date range"
              value={dateRange}
              onChange={handleDateRangeChange}
            >
              <MenuItem value={"7days"}>Last 7 days</MenuItem>
              <MenuItem value={"14days"}>Last 14 days</MenuItem>
              <MenuItem value={"30days"}>Last 30 days</MenuItem>
              <MenuItem value={"3months"}>Last 3 months</MenuItem>
              <MenuItem value={"cutoff"}>Cutoff</MenuItem>
            </SelectDropdown>
          </Grid>
  */}
          <Grid item sx={{ ml: 3 }}>
            <OutlinedButton
              btnTitle="Input Date Range"
              onClick={props.primaryButton}
            />
          </Grid>
          {/*
          <Grid item sx={{ ml: 3 }}>
            <OutlinedButton btnTitle="Reset Query" onClick={handleReset} />
          </Grid>
*/}
        </Grid>
        <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
          {isSuperAdmin && (
            <Grid item>
              <Button // [EPSv5_Manage-check in/out]
                onClick={() => setEmployeeModalOpen(true)}
                size="medium"
                sx={downloadStyle}
                variant="contained"
                startIcon={<WorkIcon />}
              >
                Check In/Out
              </Button>
            </Grid>
          )}
          {isSuperAdmin && (
            <Grid item sx={{ ml: 3 }}>
              <Button // [EPSv5_Manage-add timesheet]
                onClick={() => setAddTimesheetModalOpen(true)}
                size="medium"
                sx={downloadStyle}
                variant="contained"
                startIcon={<AddIcon />}
              >
                Add Timesheet
              </Button>
            </Grid>
          )}
          <Grid item sx={{ ml: 3 }}>
            <Button
              size="medium"
              sx={downloadStyle}
              variant="contained"
              startIcon={<DownloadIcon />}
            >
              <CSVLink
                data={cleanTimesheet}
                filename={'timesheet.csv'}
                style={{ textDecoration: 'none', color: '#FFF' }}
              >
                Export CSV
              </CSVLink>
            </Button>
          </Grid>
        </Grid>
      </QueryContainer>
      <GeneralTable
        isDataLoading={props.isLoading}
        sx={{
          borderRadius: '5px',
          boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
          mb: 3.6,
        }}
        size="small"
        length={timesheetList.length}
        entryPerPage={entryPerPage}
        page={currentPage}
        pageCount={pageCount}
        handlePageChange={handlePageChange}
        entriesPerPageComponent={
          <SelectDropdown
            sx={{ minWidth: 80 }}
            size="small"
            label="Entries"
            value={entryPerPage}
            onChange={(e) => setEntryPerPage(e.target.value)}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </SelectDropdown>
        }
      >
        <TableHead>
          <TableRow>
            <CustomTableCell value="Last Name" />
            <CustomTableCell value="First Name" />
            <CustomTableCell align="left" value="Date" />
            <CustomTableCell align="left" value="Check In" />
            <CustomTableCell align="left" value="Check Out" />
            <CustomTableCell align="left" value="Computed Time" />
            <CustomTableCell align="left" value="Total Hours" />
            <CustomTableCell align="left" value="Work Setup" />
            <CustomTableCell align="left" value="Work Description" />
            {isSuperAdmin && <CustomTableCell align="center" value="Actions" />}
          </TableRow>
        </TableHead>
        <TableBody>
          {timesheetList.slice(slice[0], slice[1]).map((ts, index) => (
            <CustomTableRow key={index}>
              <CustomTableCell scope="row" value={ts.lastname} />
              <CustomTableCell value={ts.firstname} />
              <CustomTableCell
                value={format(new Date(ts.checkin), 'dd MMM yyyy')}
              />
              <CustomTableCell
                value={format(new Date(ts.checkin), timeFormat)}
              />
              <CustomTableCell
                value={format(new Date(ts.checkout), timeFormat)}
              />
              <CustomTableCell
                value={Math.floor((ts.roundedtime / 60) * 1000) / 1000}
              />
              <CustomTableCell
                value={`${minutesToHours(ts.roundedtime)} hours ${
                  Math.floor((ts.roundedtime % 60) * 1000000) / 1000000
                } minutes`}
              />
              <CustomTableCell value={workSetup[ts.worksetup]} />
              <CustomTableCell value={ts.workdesc} />
              {isSuperAdmin && (
                <ActionTableCell>
                  <IconButton
                    id={
                      (index + entryPerPage) * (currentPage - 1) -
                      (currentPage - 2) * index
                    }
                    size="small"
                    onClick={handleEditModalOpen}
                  >
                    <EditIcon fontSize="inherit" />
                  </IconButton>
                </ActionTableCell>
              )}
            </CustomTableRow>
          ))}
        </TableBody>
      </GeneralTable>
      <GeneralModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
      >
        <Typography sx={{ mb: 2, fontWeight: 600 }} variant="h5">
          Edit timesheet:
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            renderInput={(props) => (
              <TextField
                {...props}
                margin="normal"
                fullWidth
                error={!isValid}
                helperText={
                  !isValid
                    ? 'Invalid check in. It must be before check out time.'
                    : ''
                }
              />
            )}
            label="Check In"
            value={enteredCheckIn}
            onChange={(newValue) => {
              setEnteredCheckIn(newValue);
              if (isBefore(new Date(newValue), new Date(enteredCheckOut))) {
                setIsValid(true);
              } else {
                setIsValid(false);
              }
            }}
          />
          <DateTimePicker
            renderInput={(props) => (
              <TextField
                {...props}
                margin="normal"
                error={!isValid}
                helperText={
                  !isValid
                    ? 'Invalid check out. It must be after check in time.'
                    : ''
                }
                fullWidth
              />
            )}
            label="Check Out"
            value={enteredCheckOut}
            onChange={(newValue) => {
              setEnteredCheckOut(newValue);
              if (isBefore(new Date(enteredCheckIn), new Date(newValue))) {
                setIsValid(true);
              } else {
                setIsValid(false);
              }
            }}
          />
        </LocalizationProvider>
        <SelectDropdown
          required={true}
          sx={{ minWidth: 200 }}
          label="Work Setup"
          margin="normal"
          fullWidth
          value={selectedWorkSetup}
          onChange={(event) => {
            setSelectedWorkSetup(event.target.value);
            setIsSelected(true);
          }}
        >
          <MenuItem value={1}>On-site</MenuItem>
          <MenuItem value={2}>WFH</MenuItem>
        </SelectDropdown>
        <CustomTextField
          error={!isAllowed}
          errorMsg={
            'Invalid work description. Only alphanumeric and .,:;-() are allowed.'
          }
          label={'Work Description'}
          value={enteredWorkDesc}
          onChange={(event) => {
            const workDesc = event.target.value;
            setEnteredWorkDesc(workDesc);
            workDesc.match(workDescRegex) == workDesc
              ? setIsAllowed(true)
              : setIsAllowed(false);
          }}
          required={true}
        />
        <ModalButton
          primaryBtnName="Save"
          secondaryBtnName="Cancel"
          secondaryClickHandler={() => setEditModalOpen(false)}
          primaryClickHandler={handleSaveEditedTimesheet}
          disable={!isValid || !isSelected || !isAllowed}
        />
      </GeneralModal>
      <GeneralModal
        open={employeeModalOpen}
        onClose={() => setEmployeeModalOpen(false)}
      >
        <Typography sx={{ mb: 1, fontWeight: 600 }} variant="h5">
          Select Employee:
        </Typography>
        <SelectDropdown
          margin="normal"
          required
          fullWidth
          label="Employee"
          value={empid}
          onChange={(event) => {
            setEmpId(event.target.value.trim());
          }}
        >
          {empData.map((emp) => (
            <MenuItem key={emp._id} value={emp._id}>
              {emp.lastname + ', ' + emp.firstname}
            </MenuItem>
          ))}
        </SelectDropdown>

        <ModalButton
          primaryBtnName="Okay"
          secondaryBtnName="Cancel"
          secondaryClickHandler={() => setEmployeeModalOpen(false)}
          primaryClickHandler={() => {
            isEmployeeCheckIn();
            setCheckInOutModalOpen(true);
            setEmployeeModalOpen(false);
          }}
        />
      </GeneralModal>
      <GeneralModal
        open={checkInOutModalOpen}
        onClose={() => setCheckInOutModalOpen(false)}
      >
        <Typography sx={{ mb: 1, fontWeight: 600 }} variant="h5">
          Check In/Out Employee:
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            renderInput={(props) => (
              <TextField
                {...props}
                margin="normal"
                fullWidth
                required={!isActive} // isCheckIn
                disabled
                helperText={
                  isActive
                    ? 'Employee is already checked in'
                    : 'Check in employee now'
                }
              />
            )}
            disabled
            label={isActive ? 'Check Out' : 'Check In'}
            value={addCheckIn}
            onChange={(newValue) => {
              setAddCheckIn(newValue);
            }}
          />
        </LocalizationProvider>
        {!isActive && (
          <SelectDropdown
            required={true}
            sx={{ minWidth: 200 }}
            label="Work Setup"
            margin="normal"
            fullWidth
            value={addWorkSetup}
            onChange={(event) => {
              setAddWorkSetup(event.target.value);
              setHasWorksetup(true);
              setRestrictCheckIn(false);
            }}
          >
            <MenuItem value={1}>On-site</MenuItem>
            <MenuItem value={2}>WFH</MenuItem>
          </SelectDropdown>
        )}
        {isActive && (
          <CustomTextField
            error={!isValidDesc}
            errorMsg={
              'Invalid work description. Only alphanumeric and .,:;-() are allowed.'
            }
            label={'Work Description'}
            value={addWorkDesc}
            onChange={(event) => {
              const workDesc = event.target.value;
              setAddWorkDesc(workDesc);
              workDesc.match(workDescRegex) == workDesc
                ? setIsValidDesc(true)
                : setIsValidDesc(false);
            }}
          />
        )}
        {restrictCheckIn && (
          <Alert
            severity="error"
            variant="filled"
            sx={{
              bgcolor: 'background.paper',
              mb: 2,
              pl: 1,
              py: 0,
              mt: 0,
              justifyContent: 'left',
            }}
          >
            {addWorkSetup == 1
              ? 'Earliest time in is 06:00 AM. Please try again later.'
              : 'Earliest time in is 08:00 AM. Please try again later.'}
          </Alert>
        )}
        <ContainedButton
          style={
            isActive
              ? {
                  width: '100%',
                  mt: 2,
                  py: 1,
                  bgcolor: '#d5615d',
                  border: '1px solid #d5615d',
                  '&:hover': {
                    bgcolor: 'rgb(173, 59, 55) !important',
                  },
                }
              : { width: '100%', mt: 2, py: 1 }
          }
          btnTitle={isActive ? 'Check Out Employee' : 'Check In Employee'}
          onClick={handleLoading}
          disabled={isActive ? !isValidDesc : !hasWorksetup}
        />
      </GeneralModal>
      <AddTimesheetModal
        open={addTimesheetModalOpen}
        onClose={() => setAddTimesheetModalOpen(false)}
        allEmpData={empData}
        onSave={handleAddNewTimesheet}
        onSubmit={() => setIsSubmitting(true)}
      />
      <SuccessModal
        open={addSuccessOpen}
        onClose={() => {
          setAddSuccessOpen(false);
          window.location.reload();
        }}
        close={() => {
          setAddSuccessOpen(false);
          window.location.reload();
        }}
        desc={successMessage}
      />
      <SuccessModal
        open={resultModalOpen}
        onClose={() => {
          setResultModalOpen(false);
          window.location.reload();
        }}
        close={() => {
          setResultModalOpen(false);
          window.location.reload();
        }}
        desc={'You have successfully edited a timesheet record.'}
      />
      <SuccessModal
        open={newSuccessOpen}
        onClose={() => {
          setNewSuccessOpen(false);
          window.location.reload();
        }}
        close={() => {
          setNewSuccessOpen(false);
          window.location.reload();
        }}
        desc={'You have successfully added a timesheet record.'}
      />{' '}
    </>
  );
};
